import React, { JSX, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";

import useFetch from "utils/fetch/useFetch";
import { ListInfo } from "utils/list/List";
import { isTrajectXUser } from "utils/UserUtils";
import { asTitle } from "utils/Utils";

import { usePrincipal } from "authentication/AuthProvider";

import BlueprintActionbar from "core/blueprint/base/BlueprintActionbar";
import BlueprintFilter, { Filter } from "core/blueprint/base/BlueprintFilter";
import {
	BlueprintInfo,
	getBlueprintRights,
	getBlueprints,
	Right,
	UserRights
} from "core/blueprint/base/BlueprintService";
import BlueprintTable from "core/blueprint/base/BlueprintTable";
import CopyBlueprintDialog from "core/blueprint/base/dialog/CopyBlueprintDialog";
import CreateBlueprintDialog from "core/blueprint/base/dialog/CreateBlueprintDialog";
import DeleteBlueprintDialog from "core/blueprint/base/dialog/DeleteBlueprintDialog";
import EditBlueprintDialog from "core/blueprint/base/dialog/EditBlueprintDialog";
import ImportBlueprintDialog from "core/blueprint/base/dialog/ImportBlueprintDialog";
import InfoBlueprintDialog from "core/blueprint/base/dialog/InfoBlueprintDialog";
import LibraryActionbar from "core/blueprint/base/LibraryActionbar";
import { getBlueprintLink } from "core/blueprint/base/Utils";
import LibraryClient from "core/library/LibraryClient";

enum DialogType {
	INFO,
	CREATE,
	EDIT,
	COPY,
	DELETE,
	IMPORT
}

type DialogInfo = {
	blueprint: BlueprintInfo;
	userRights: UserRights[];
};

const BlueprintOverview: React.FC = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { call } = useFetch();

	const principal = usePrincipal();

	const [blueprints, setBlueprints] = useState<BlueprintInfo[]>([]);
	const [listInfo, setListInfo] = useState<ListInfo<number>>(ListInfo.init());

	const [dialogType, setDialogType] = useState<DialogType>();
	const [dialogInfo, setDialogInfo] = useState<DialogInfo>();

	useEffect(() => fetchBlueprints(), []);

	useEffect(() => {
		setListInfo(prevInfo => prevInfo.withKeys(blueprints.map(b => b.id)));
	}, [blueprints]);

	function fetchBlueprints(): void {
		getBlueprints().then(result => {
			setBlueprints(result.sort((a, b) => a.name.localeCompare(b.name)));
		});
	}

	function selectBlueprints(keys: number[], select: boolean): void {
		setListInfo(listInfo.markSelected(keys, select));
	}

	function editOptionsEnabled(): boolean {
		const bpKeys = listInfo.getVisibleAndSelectedKeys();
		if (bpKeys.length !== 1) {
			return false;
		}

		const bp = blueprints.find(bp => bp.id === bpKeys[0]);
		return bp?.rights.includes(Right.ADMIN) ?? false;
	}

	function readOptionsEnabled(): boolean {
		const bpKeys = listInfo.getVisibleAndSelectedKeys();
		if (bpKeys.length !== 1) {
			return false;
		}

		const bp = blueprints.find(bp => bp.id === bpKeys[0]);
		return bp?.rights.includes(Right.READ) ?? false;
	}

	function deleteOptionEnabled(): boolean {
		const bpKeys = listInfo.getVisibleAndSelectedKeys();
		if (bpKeys.length === 0) {
			return false;
		}

		return blueprints
			.filter(bp => bpKeys.includes(bp.id))
			.every(bp => bp.rights.includes(Right.ADMIN));
	}

	function getVisibileSelectedBlueprint(): BlueprintInfo | undefined {
		const bpKey = listInfo.getVisibleAndSelectedKeys()[0];
		return blueprints.find(bp => bp.id === bpKey);
	}

	function showEditDialog(type: DialogType) {
		const bp = getVisibileSelectedBlueprint();
		if (bp === undefined) {
			return;
		}

		getBlueprintRights(bp.id).then(rights => {
			setDialogType(type);
			setDialogInfo({
				blueprint: bp,
				userRights: rights
			});
		});
	}

	function handleEdit(): void {
		showEditDialog(DialogType.EDIT);
	}

	function handleCopy(): void {
		showEditDialog(DialogType.COPY);
	}

	function handleNew(): void {
		setDialogType(DialogType.CREATE);
	}

	function handleDelete(): void {
		setDialogType(DialogType.DELETE);
	}

	function handleShowInfo(): void {
		showEditDialog(DialogType.INFO);
	}

	function closeDialog(): void {
		setDialogType(undefined);
		setDialogInfo(undefined);
	}

	function refreshContent(): void {
		closeDialog();
		fetchBlueprints();
	}

	function infoDialog(): JSX.Element {
		if (dialogInfo === undefined) {
			return <></>;
		}

		return (
			<InfoBlueprintDialog
				info={dialogInfo.blueprint}
				userRights={dialogInfo.userRights}
				onClose={closeDialog}
			/>
		);
	}

	const applyFilter = useCallback((filter: Filter): void => {
		const matchingIds = blueprints
			.filter(bp => {
				let matches = true;
				if (filter.type !== "") {
					matches = matches && bp.type === filter.type;
				}
				if (filter.name !== "") {
					matches =
						matches &&
						bp.name
							.toLowerCase()
							.includes(filter.name.toLowerCase());
				}
				if (filter.description !== "") {
					matches =
						matches &&
						bp.description
							.toLowerCase()
							.includes(filter.description.toLowerCase());
				}
				return matches;
			})
			.map(bp => bp.id);

		setListInfo(previous => previous.setVisible(matchingIds));
	}, [blueprints]);

	function createDialog(): JSX.Element {
		return (
			<CreateBlueprintDialog
				onClose={closeDialog}
				onConfirm={(id, type) => navigate(getBlueprintLink(id, type))}
			/>
		);
	}

	function editDialog(): JSX.Element {
		if (dialogInfo === undefined) {
			return <></>;
		}

		return (
			<EditBlueprintDialog
				info={dialogInfo.blueprint}
				userRights={dialogInfo.userRights}
				onClose={closeDialog}
				onConfirm={refreshContent}
			/>
		);
	}

	function copyDialog(): JSX.Element {
		if (dialogInfo === undefined) {
			return <></>;
		}

		return (
			<CopyBlueprintDialog
				info={dialogInfo.blueprint}
				userRights={dialogInfo.userRights}
				onClose={closeDialog}
				onConfirm={refreshContent}
			/>
		);
	}

	function deleteDialog(): JSX.Element {
		return (
			<DeleteBlueprintDialog
				ids={listInfo.getVisibleAndSelectedKeys()}
				onClose={closeDialog}
				onConfirm={refreshContent}
			/>
		);
	}

	function handleMakePublic(pub: boolean): void {
		const func = pub ? LibraryClient.add : LibraryClient.remove;

		Promise.all(
			listInfo.getVisibleAndSelectedKeys().map(id => call(func(id)))
		).then(refreshContent);
	}

	function handleImport(): void {
		setDialogType(DialogType.IMPORT);
	}

	function importDialog(): JSX.Element {
		return (
			<ImportBlueprintDialog
				onClose={closeDialog}
				onConfirm={refreshContent}
			/>
		);
	}

	return (
		<>
			<Typography id="title" variant="h1">
				{asTitle(t("blueprint_other"))}
			</Typography>

			<Grid container columnSpacing={2}>
				<Grid item>
					<BlueprintActionbar
						copyEnabled={editOptionsEnabled()}
						editEnabled={editOptionsEnabled()}
						infoEnabled={readOptionsEnabled()}
						deleteEnabled={deleteOptionEnabled()}
						onRefresh={refreshContent}
						onNew={handleNew}
						onCopy={handleCopy}
						onEdit={handleEdit}
						onShowInfo={handleShowInfo}
						onDelete={handleDelete}
					/>
				</Grid>

				{isTrajectXUser(principal?.userEmail ?? "") && (
					<Grid item>
						<LibraryActionbar
							enabled={deleteOptionEnabled()}
							onMakePublic={handleMakePublic}
							onImport={handleImport}
						/>
					</Grid>
				)}
			</Grid>

			<BlueprintFilter onChange={applyFilter} />

			<BlueprintTable
				elements={blueprints}
				listInfo={listInfo}
				showLibraryStatus={
					principal?.userEmail !== undefined &&
					isTrajectXUser(principal?.userEmail)
				}
				onSelect={selectBlueprints}
			/>

			{dialogType === DialogType.INFO && infoDialog()}
			{dialogType === DialogType.CREATE && createDialog()}
			{dialogType === DialogType.COPY && copyDialog()}
			{dialogType === DialogType.EDIT && editDialog()}
			{dialogType === DialogType.DELETE && deleteDialog()}
			{dialogType === DialogType.IMPORT && importDialog()}
		</>
	);
};

export default BlueprintOverview;
